import { useEffect, useState } from "react";
import JobStatusPanel, { JobUpdatePayload } from "./JobStatusPanel";
import { useJobs } from "../providers/JobsProvider";
import { JobService } from "../services/JobService";

import "./styles/Jobs.css";
import Pagination from "./Pagination";

export interface JobStatusesProps {
  forceClientFilename?: boolean;
  className?: string;
}

export default function Jobs({
  forceClientFilename = false,
  className,
}: JobStatusesProps) {
  const { jobs, connected, watchJobs } = useJobs();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [pageJobIds, setPageJobIds] = useState<string[]>([]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const fetchJobs = async () => {
      setIsLoading(true);
      try {
        const jobsResponse = await JobService.getJobs(currentPage);

        if (jobsResponse.error || !jobsResponse.data) {
          // You could surface a toast/error UI here if you have one
          setPageJobIds([]);
          return;
        }

        const { jobs: pagedJobs, totalPages: tp } = jobsResponse.data;

        const pagedJobIds = pagedJobs.map(j => j.id);

        setPageJobIds(pagedJobIds);

        if (tp) {
          setTotalPages(tp);
        }

        unsubscribe = await watchJobs(pagedJobIds);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();

    return () => {
      unsubscribe?.();
    }
  }, [currentPage, watchJobs]);

  useEffect(() => {
    // Smooth scroll to top whenever page changes
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentPage]);

  const hasJobs = pageJobIds.length > 0;

  return (
    <div className={`jobs-root ${className ?? ""}`}>
      {/* Header */}
      <div className="jobs-header">
        <h1 className="jobs-title">Jobs</h1>
        {hasJobs && (
          <span className="jobs-count">
            Page {currentPage} of {totalPages}
          </span>
        )}
      </div>

      {/* Loading state (initial) */}
      {isLoading && !hasJobs && (
        <div className="jobs-loading">Loading jobs…</div>
      )}

      {/* Empty state */}
      {!isLoading && !hasJobs ? (
        <div className="jobs-empty">
          <div className="jobs-empty-icon" aria-hidden="true">
            📼
          </div>
          <h2 className="jobs-empty-title">No jobs yet</h2>
          <p className="jobs-empty-subtitle">
            Start a new cut and it will show up here when it’s queued.
          </p>
        </div>
      ) : null}

      {/* List */}
      {hasJobs && (
        <>
          <div className="jobs-list">
            {pageJobIds.map((jobId) => (
              <JobStatusPanel
                key={jobId}
                jobId={jobId}
                payload={(jobs[jobId] as JobUpdatePayload | null) ?? null}
                connected={connected}
                forceClientFilename={forceClientFilename}
              />
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
}
